import _ from 'lodash';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import ListJobNames, { ListJob } from './listJobNames';
import constants from '../../constants';
import useJobMenu from '../../hooks/useJobMenu';
import ScreenTitleBar from '../../uiControls/screens/ScreenTitleBar';
import useFilterBar from '../../hooks/useFilterBar';
import useFunctionKeys from '../../hooks/useFunctionKeys';
import useListData from '../../hooks/useListData';
import useGetEditedId from '../../hooks/useGetEditedId';
import { ApplicableTableSettings, createColumnFocusListHierarchy, TableColumn } from '../../tableSettings';
import SmartTable, { SmartTableHandle } from '../../uiControls/SmartTable';
import useGlobalOkDialog from '../../hooks/useGlobalOkDialog';
import ApplicationContext from '../../ApplicationContext';

import JobListViewContext, { IJobListViewContext } from './JobListViewContext';
import { TrackingRequestModalState } from '../../util/trackingRequestUtils';
import useSystemSettings from '../../hooks/useSystemSettings';
import useSuntekSettings from '../../hooks/useSuntekSettings';
import useDeepCompareEffect from '../../hooks/useDeepCompareEffect';
import { isFilterObjectEmpty } from '../../filtering';
import { useTypedFastStorage } from '../../hooks/useFastStorage';
import BlackInverseButton from '../../ui/BlackInverseButton';

import excelIcon from '../../img/toolbar-excel-light.svg';
import excelIconGreen from '../../img/toolbar-excel-dark.svg';
import excelIcon2 from '../../img/excel_light.svg';
import excelIconGreen2 from '../../img/excel_dark.svg';
import { exportDomTableIntoExcel } from '../../excel';
import { ListOrderBy, ViewUrl } from './AbstractListView';
import { XCellType } from '../../framework.core/XExportColumn';

import { compareIso } from '../../dateUtils';
import useStorage from '../../hooks/useStorage';
import config from '../../config';
import { useContextMenu } from '../../hooks/useContextMenu';
import { getSecBySecTask } from '../../util';
import { JobNames } from '../../framework.core/JobNames';
import IconButton from '../../ui/IconButton';
import MemoIcon2 from '../../img/bo_note_D2.svg';
import MemoIcon2_D from '../../img/bo_note_D2_D.svg';
import JobSwitchingMenuContext from '../jobSwitchingMenuContext';
import { SchBackColor, StatusBackColor } from './ShipmentStatus';
import fastStorage from '../../fastStorage';
import { FmsSecTask } from '../../fms.core/constants';
import { SecType, SiteLogType } from '../../fms.enum';
import { getFmsGlobal } from '../../globals';
import { AI_CENTER_CONTACT_SUPPORT, isAiCenterOff, useAiCenterFlags } from '../../aiCenterFlags';

export const listViewPageSize: number = 30;
export const listViewPagePerBatch: number = 10;

const AllowedOpTypes = _.keys(constants.operations);

type JobListViewParams = {
  jobName: string;
  opType: string | undefined;
  parentOp: string | undefined;
};

/**
 * Gets operation name from property name (if exists.) For example oceanImport is mapped to 'Ocean Import'
 *
 * @param {(string|undefined)} opType
 * @return {*}  {(string|null)}
 */
function getOperationNameFromOpType(opType: string | undefined): string | null {
  if (!opType) {
    return null;
  }

  if (!AllowedOpTypes.some(k => k === opType)) {
    throw new Error(`Invalid opType. Must be either undefined or one of: [${AllowedOpTypes.join(', ')}]`)
  }

  return constants.operations[opType];
}

/**
 * Gets job object from name extracted from route
 *
 * @param {string} name
 * @return {*}  {Job}
 */
export function getJobFromName(name: string): ListJob {
  const result = ListJobNames[name]
  if (!result) {
    throw new Error('Invalid route');
  }
  return result;
}

interface ListSortResult {
  sorted: boolean,
  data: Array<any>,
}

function getColumnDataType(c: TableColumn): XCellType {
  return c.filterTypeV2 ?? XCellType.String;
}

function sortListData(data: Array<any>, orderBy: ListOrderBy | null, columnSettings: Array<TableColumn>): ListSortResult {
  if (!orderBy) {
    return { sorted: false, data: [] };
  }

  const sortCol = columnSettings.find(x => x.accessor === orderBy.orderBy);
  if (!sortCol) {
    return { sorted: false, data: [] };
  }

  const dataType = getColumnDataType(sortCol);

  const comparer = (a: any, b: any): number => {
    const left = orderBy.orderByAscending ? a[sortCol.accessor] : b[sortCol.accessor];
    const right = orderBy.orderByAscending ? b[sortCol.accessor] : a[sortCol.accessor];

    if (dataType === XCellType.Double) {
      return (left ?? 0) - (right ?? 0);
    }
    if (dataType === XCellType.Bool || dataType === XCellType.StrBool) {
      if (left === right) {
        return 0;
      }
      return left && !right ? 1 : -1;
    }
    if (dataType === XCellType.Date || dataType === XCellType.DateTime) {
      if (!left) {
        return !right ? 0 : -1;
      }
      if (!right) {
        return 1;
      }

      return compareIso(left as string, right as string);
    }

    const leftString: string = left as string ?? '';
    const rightString: string = right as string ?? '';

    return leftString.localeCompare(rightString);
  }

  const result = [...data];
  result.sort(comparer);

  return { sorted: true, data: result };
}

/**
 * Generic Job List View Component. Internally it constructs concrete class with needed implementations to display on the screen.
 * Implementation is constructed based on route params.
 *
 * @return {*} 
 */
function JobListViewImpl({ jobName, opType, parentOp }: JobListViewParams) {
  const operationName = getOperationNameFromOpType(opType);

  const job = getJobFromName(jobName);
  const listView = job.generateListView();

  const [showGlobalOkDialog] = useGlobalOkDialog();
  const getSavedEntity = useGetEditedId();

  // AI Center: AI Settings 에서 꺼져 있으면 목록 주소로 바로 들어와도 열지 않는다 — 메뉴 클릭과 같은 경고 후 닫는다 (2026-10-06)
  const aiCenterFlags = useAiCenterFlags();
  useEffect(() => {
    if (jobName !== JobNames.AICenter || !isAiCenterOff(aiCenterFlags)) return;
    (async () => {
      await showGlobalOkDialog(<p>{AI_CENTER_CONTACT_SUPPORT}</p>, false, null, 'warning');
      closeScreen();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobName, aiCenterFlags]);

  const appContext = useContext(ApplicationContext);
  const systemSettings = useSystemSettings();
  const suntekSettings = useSuntekSettings();

  const title = listView.getListViewTitle(opType);

  listView.setSystemSettings(systemSettings);
  listView.setSuntekSettings(suntekSettings);
  listView.setUser(appContext.user);

  const listDomId = `tbl-list-${jobName}-${opType ?? 'common'}`;
  const listDomWrapperId = `tbl-list-wrapper-${jobName}-${opType ?? 'common'}`;

  const history = useHistory();

  const [listOrderBy, setListOrderBy, deleteListOrderBy] = useTypedFastStorage<ListOrderBy>(`ifms-list-data-${listDomId}-order-by`, listView.getDefaultOrderBy());
  const [pageNum, setPageNum, deletePageNum, pageNumRef] = useTypedFastStorage<number>(`ifms-list-data-${listDomId}-pagenum`, 1);

  const { FilterButton, search, clearFilters, clearFiltersFromStorage, setFilterForScreenExternally, setFilterForSearchModel, openedByF6, f6Origin, f6Query, setListFilterType, setListFilterType2, setListFilterType3, setListFilterType4 } = useFilterBar(
    listView.getTableSettings(opType, systemSettings, suntekSettings),
    listView.getCustomFilterRenderers(opType, systemSettings, suntekSettings),
    listView.getDefaultOrderBy(),
    '',
    operationName === 'My Event',
    setPageNum,
    {
      pageSize: listViewPageSize,
      pagePerBatch: listViewPagePerBatch,
    },
    setListOrderBy,
  );
  useFunctionKeys({ onEsc: scheduleEscProcess });

  const parentOpName = getOperationNameFromOpType(parentOp);
  const noOperationSuffixMenu = (!!parentOpName && parentOpName !== 'Other Shipment') || opType === 'accounting' || (opType === 'otherShipment' && jobName === 'otherShipment');
  const { canGoBack, goBack,
    hasOpenedScreenUrl, hasOpenedScreenStartingWithUrl, hasOpenedScreenStartingWithUrlWithoutMe,
    hasOpenedScreenInAnyOperationStartingWithUrl, hasOpenedScreenInAnyOperationStartingWithUrlWithoutMe,
    getOpenedScreenInAnyOperationStartingWithUrl,
    moveToOpenedScreenInAnyOperationStartingWithUrl }
    = useJobMenu(title, operationName, null, false, cleanup, null, { inputId: '', isLoading: false }, noOperationSuffixMenu, parentOpName);

  const [initialized, setInitialized] = useState(false);
  const headerClickRef = useRef(true);

  const { onCellRightClick, RightClickContextMenu } = useContextMenu(listView);
  const {
    data,
    dataRef,
    setData,
    setSelectedId,
    getSelectedIndex,
    clearListData,
    selectTableRow,
    triggerTableSearch,
    currentCount,
  }
    = useListData(listDomId, onRowDoubleClick, createColumnFocusListHierarchy(listView.getTableSettings(opType, systemSettings, suntekSettings)), onCellRightClick, initialized, () => { headerClickRef.current = true }, { pageSize: listViewPageSize, pagePerBatch: listViewPagePerBatch });

  const tableRef = useRef<SmartTableHandle>(null);
  const [tick, setTick] = useState(0);
  const clearFilterRef = useRef(false); //clearFilters()가 호출될때 list를 reload하기 위해 추가

  const currentUrl = `${history.location.pathname}${history.location.search}`;
  //const infoAreaModelKey = `info-area-${currentUrl}`;
  const hideLogHistoryModelKey = `hide-log-${currentUrl}`;
  const context = useContext(JobSwitchingMenuContext);

  // let initializeInfoAreaValue = 'none';
  // if (appContext.appInfoAreaContent !== 'none') {
  //   if (appContext.appInfoAreaContent === 'memo' && !listView.hasMemo()) {
  //     initializeInfoAreaValue = 'none';
  //   } else {
  //     initializeInfoAreaValue = appContext.appInfoAreaContent;
  //   }
  // }

  //const [infoAreaContent, _setInfoAreaContent, deleteInfoAreaContent, infoAreaContentRef] = useTypedFastStorage<string>(infoAreaModelKey, initializeInfoAreaValue);
  const [hideLogHistory, , deleteHideLogHistory] = useStorage(hideLogHistoryModelKey, false);

  const [escScheduled, setEscScheduled] = useState(false);
  const [rowExtrasId, setRowExtrasId] = useState<number | null>(null);
  const clickedCellRef = useRef(null);
  const [trackingRequestModalState, setTrackingRequestModalState] = useState<TrackingRequestModalState | undefined>(undefined);
  const [modalShow, setmodalShow] = useState<string | undefined>(undefined);
  const [recogModalData, setRecogModalData] = useState<{ isSet: boolean, isOpen: boolean }>({ isSet: false, isOpen: false });

  const selectedIndex = getSelectedIndex();
  const hasSelectedRow = selectedIndex >= 0;
  const selectedElement = hasSelectedRow ? data[selectedIndex] : null;
  const [viewPopupRow, setViewPopupRow] = useState<any | undefined>(undefined);
  const isMouseOverRef = useRef(false);
  const headerOrderByText = useRef('');
  const [listFilter, setListFilter, deleteListFilter] = useTypedFastStorage<string>(`ifms-list-data-${listDomId}-listFilter`, 'A');

  const jobListViewContext: IJobListViewContext = {
    selectedElement,
    hasOpenedScreenUrl,
    hasOpenedScreenStartingWithUrl,
    hasOpenedScreenStartingWithUrlWithoutMe,
    hasOpenedScreenInAnyOperationStartingWithUrl,
    hasOpenedScreenInAnyOperationStartingWithUrlWithoutMe,
    getOpenedScreenInAnyOperationStartingWithUrl,
    moveToOpenedScreenInAnyOperationStartingWithUrl,
    reloadData: reloadData,
    triggerTableSearch,
    setFilterForScreenExternally,
    setFilterForSearchModel,
    // infoAreaContent: listScreenSettings.showMemo ? 'memo' : 'none',
    //infoAreaContent: infoAreaContent ?? 'none',
    infoAreaContent: (listView.hasMemo() ? (appContext.appInfoAreaContent ?? 'none') : 'none'),
    setInfoAreaContent,
    updateDataRow,
    openedByF6,
    f6Origin,
    f6Query,
    rowExtrasId,
    setRowExtrasId,
    setClickedCell: (data) => { clickedCellRef.current = data },
    setData,
    data,
    viewPopupRow,
    dismissPopupRow: () => setViewPopupRow(undefined),
    showPopupRow,
    opType,
    search,
    setListFilterType,
    trackingRequestModalState,
    setTrackingRequestModalState,
    modalShow,
    setmodalShow,
    recogModalData,
    setRecogModalData,
    setListFilterType2,
    setListFilterType3,
    setListFilterType4,
    changeListData,
    listFilter: listFilter,
    setListFilter: (type: string) => setListFilter(type as any),
  };

  useEffect(() => {
    if (!data || data.length === 0) {
      loadDataWithCurrentFilterSettings(1, true);
      deleteListFilter();
    }
    else headerClickRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!escScheduled) {
      return;
    }
    closeScreen();
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [escScheduled]
  );

  useDeepCompareEffect(() => {
    setTick(tick + 1);
    refreshUpdatedEntity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (initialized && data.length > 0 && selectedIndex !== -1) {
      setTimeout(() => {
        selectTableRow(selectedIndex, headerClickRef.current);
      }, 10);
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIndex, initialized, tick]
  );

  // View를 이동할 때도 headerOrderByText.current가 유지돼야함
  useEffect(() => {
    if (!listOrderBy || !listOrderBy.orderBy) return;
    updateHeaderOrderByText(listOrderBy.orderBy, listOrderBy.orderByAscending);
  }, [listOrderBy]);
  
  useEffect(() => {
    const isEmptyFilter = isFilterObjectEmpty(search);
    
    if (!search || !initialized || !pageNum || (!search.isSearchByReport && isEmptyFilter && search.version === 1 && search.listType === undefined)) {
      if(clearFilterRef.current) {
        reloadData();
      } 
      else return;
    }

    if (isEmptyFilter) {
       setSelectedId(null);
    } 
    
    if(!clearFilterRef.current) reloadData(false);
    clearFilterRef.current = false; //false로 초기화
  }, [search]);

  function updateHeaderOrderByText(orderBy: string, orderByAscending: boolean) {
    if (!orderBy) return;
    const orderStr = orderByAscending
      ? `${orderBy} ASC`
      : `${orderBy} DESC`;
    headerOrderByText.current = orderStr;
  }

  async function reloadData(clearHeaderOrder: boolean = true) {
    if (clearHeaderOrder) {
      if (isFilterObjectEmpty(search)) {
        headerOrderByText.current = '';
        onFilterClear();
      }
      else {
        updateHeaderOrderByText(search.orderBy, search.orderByAscending);
        setListOrderBy({ orderBy: search.orderBy, orderByAscending: search.orderByAscending });
      }
    }
    headerClickRef.current = false;
    await loadDataWithCurrentFilterSettings(1, true, clearHeaderOrder);
  }

  /**
   * Sets info area content type. Info area is rendering is defined in concrete implementation of listView instance if it is need.
   * Job list view just keeps track of currently active info area section.
   *
   * @param {string} c
   */
  function setInfoAreaContent(c: string) {
    const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
    if (modals.length > 0) {
      return false;
    }
    // Currently no op - info area is no longer 'per list screen

    appContext.setAppInfoAreaContent(c);
    if (c === 'memo' && !listView.hasMemo()) return;
    // if (infoAreaContentRef.current === c) {
    //   _setInfoAreaContent('none');
    //   // infoAreaContentRef.current = 'none';
    // } else {
    //   _setInfoAreaContent(c);
    //   // infoAreaContentRef.current = c;
    // }
  }

  /**
   * Loads saved entity from edit/new screen and refresh in list (in case of edit),
   * or add to top (in case of new)
   *
   */
  async function refreshUpdatedEntity() {
    const editedEntity = getSavedEntity();
    const pageNum = pageNumRef.current ?? 1;

    if (editedEntity.isDelete) {
      reloadData();
      return;
    }

    if (!editedEntity || !editedEntity.editedId) {
      return;
    }

    if (editedEntity.refreshAll) {
      reloadData();
      return;
    }

    if (editedEntity.isNew) {
      //const blockStartPage = pageNum - ((pageNum - 1) % listViewPagePerBatch);
      setPageNum(1);

      setSelectedId(data[0]?.id);
    }
  }

  function updateDataRow(row: any, insertIfNotExist: boolean = false) {
    if (!row || !row.id) {
      return;
    }
    headerClickRef.current = true;

    const newData = [...dataRef.current!];
    const editIndex = newData.findIndex(x => x.id === row.id);
    if (editIndex >= 0) {
      newData[editIndex] = row;
      setData(newData, currentCount);
    } else {
      if (insertIfNotExist) {
        setData([row, ...dataRef.current!], currentCount + 1);
      }
    }
  }

  /**
   * Loads list data from current list settings. Also makes sure all columns for which filtering is applied for are visible.
   *
   * @return {*} 
   */
  async function loadDataWithCurrentFilterSettings(pageNum: number, foceDbCall: boolean = false, clearHeaderOrder: boolean = true) {
    if (!search) {
      return;
    }
    if (headerOrderByText.current === '') updateHeaderOrderByText(search.orderBy, search.orderByAscending);
    await fetchData(appContext.api, search, pageNum, foceDbCall, clearHeaderOrder);
  }

  /**
   * Calls list view implementation to get the actual data based on given search object
   *
   * @param {*} api
   * @param {*} search
   */
  async function fetchData(api: any, search: any, pageNum: number, forceDbCall: boolean, clearHeaderOrder: boolean) {
    setInitialized(false);

    const prevPageNum = pageNumRef.current;
    const prevBlockStartPage = prevPageNum ? prevPageNum - ((prevPageNum - 1) % listViewPagePerBatch) : null;
    const prevBlockNum = prevBlockStartPage ? Math.floor((prevBlockStartPage - 1) / listViewPagePerBatch) : null;

    setPageNum(pageNum);

    const blockStartPage = pageNum - ((pageNum - 1) % listViewPagePerBatch);
    const blockNum = Math.floor((blockStartPage - 1) / listViewPagePerBatch);

    const isSameBlock = prevBlockNum === blockNum;
    const isEmptySearch = isFilterObjectEmpty(search);
    const searchWithHeaderOrder = {
      ...search,
      headerOrderBy: headerOrderByText.current,
    };

    // ---------------------------------------------------------
    // 1) 새로운 블록이면 fetch + 저장
    // ---------------------------------------------------------
    if (!isSameBlock || forceDbCall) {
      const dataFetchResult = await listView.fetchAndFormatData(
        api,
        searchWithHeaderOrder,
        blockStartPage,
        isEmptySearch ? listViewPageSize * listViewPagePerBatch : 0,
        opType,
        appContext.user,
        jobListViewContext
      );

      if ([
        JobNames.OIMPims, JobNames.OIHPims, 
        JobNames.OEMPims, JobNames.OEHPims, 
        JobNames.AIMPims, JobNames.AIHPims, 
        JobNames.AEMPims, JobNames.AEHPims
      ].includes(jobName as JobNames) && !(getFmsGlobal().PimsID)) {
        await showGlobalOkDialog(<p>To enable Agent EDI, please contact Suntek Systems INC</p>);
      }
      
      setData(dataFetchResult.data, dataFetchResult.totalCount);
      fastStorage[`${listDomId}-displayTotalDataCount`] = dataFetchResult.displayTotalCount;

      const isNotShipmentStatusFilterEmpty = !!(search.listType || search.listType2 || search.listType3 || search.listType4);

      if ((!f6Query && !isFilterObjectEmpty(search)) || (opType === "shipmentStatus" && isNotShipmentStatusFilterEmpty)) {
        if (dataFetchResult.data.length <= 0) {
          await showGlobalOkDialog(<p>No data found</p>);
          clearFilterRef.current = true;
          clearFilters();
          //setSelectedId(null);
        }
      } 
      if (clearHeaderOrder) headerOrderByText.current = '';
    }

    setInitialized(true);
  }

  async function changeListData(jobName: any, itemType: string, searchParams: any, other: string, listFilterType: any, formatData: (entities: any[]) => any[]) {
    setInitialized(false);

    const currentPageNum = pageNumRef.current ?? 1;
    setPageNum(currentPageNum);

    const blockStartPage = currentPageNum - ((currentPageNum - 1) % listViewPagePerBatch);
    const isEmptySearch = isFilterObjectEmpty(searchParams);

    const apiResult = await appContext.api.getListItems(
      jobName,
      itemType,
      searchParams,
      blockStartPage,
      isEmptySearch ? (listViewPageSize * listViewPagePerBatch) : 0,
      listViewPageSize,
      listViewPagePerBatch,
      other,
      listFilterType
    );
    
    if (!apiResult || !apiResult.entities || apiResult.entities.length <= 0) {
      setData(formatData([]), 0);
    } else {
      setData(formatData(apiResult.entities), apiResult.totalCount);
    }

    setInitialized(true);
  }
  
  /**
   * Callback function for filter clear inside filter window or by clicking on the same menu item again.
   * Reset order by by default
   *
   */
  function onFilterClear() {
    setListOrderBy(listView.getDefaultOrderBy());
  }

  /**
   * Cleanup routing before Job item is being disposed
   *
   */
  function cleanup() {
    //deleteInfoAreaContent();
    deleteHideLogHistory();
    clearListData();
    clearFiltersFromStorage();
    deleteListOrderBy();
    deletePageNum();
    deleteListFilter();
    delete fastStorage[`${listDomId}-displayTotalDataCount`];
  }

  /**
   * Schedule Esc key event handler in scope with current state
   *
   */
  function scheduleEscProcess() {
    // In case any modal window is opened, don't close the job
    const modals = document.querySelectorAll('[class^="ReactModal__Content"]');
    if (modals.length > 0) {
      return false;
    }

    setEscScheduled(true);
    return true;
  }
  /**
   * Request screen to be closed
   *
   * @return {*} 
   */
  async function closeScreen() {
    const close = await canGoBack();

    setEscScheduled(false);
    deletePageNum();
    if (!close) {
      return false;
    }

    // appContext.setJournalType(0);

    if (jobName === 'RPCSP') {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('shipStatus-cust-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
      const keyMatch2 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-status-ListFilter'));
      if (keyMatch2) {
        delete fastStorage[keyMatch2];
      }
      const keyMatch3 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-tracking-ListFilter'));
      if (keyMatch3) {
        delete fastStorage[keyMatch3];
      }
      const keyMatch4 = _.keys(fastStorage).find(x => x.startsWith('shipStatus-blType-ListFilter'));
      if (keyMatch4) {
        delete fastStorage[keyMatch4];
      }
    }

    if(jobName === JobNames.AICenter) {
      const keyMatch = _.keys(fastStorage).find(x => x.startsWith('aiCenter-result-ListFilter'));
      if (keyMatch) {
        delete fastStorage[keyMatch];
      }
    }

    goBack();

    return true;
  }

  /**
   * Show column settings dialog
   *
   */
  function initializeColumnsDialog() {
    if (tableRef && tableRef.current) {
      tableRef.current.showColumnSettings();
    }
  }

  /**
   * Request data refresh
   *
   */
  function refresh() {
    reloadData(true);
  }


  function showPopupRow() {
    if (!listView.hasViewPopup()) {
      return;
    }

    const rowIndex = getSelectedIndex();

    if (dataRef && dataRef.current && rowIndex >= 0 && rowIndex < dataRef.current.length) {
      setViewPopupRow(dataRef.current[rowIndex]);
    }
  }

  /**
   *
   * @param {number} rowIndex
   */
  async function onRowDoubleClick(rowIndex: number) {
    // This is globally attached event handler. If Search window is opened - dismiss this call
    const searchWindow = document.getElementById('ifms-list-screen-filter');
    if (searchWindow) {
      return;
    }


    if (dataRef && dataRef.current && rowIndex >= 0 && rowIndex < dataRef.current.length) {
      if (listView.hasViewPopup()) {
        setViewPopupRow(dataRef.current[rowIndex]);
        return;
      }

      let viewParams = listView.getViewUrlFor(dataRef.current[rowIndex]) as ViewUrl;
      if (_.isEmpty(viewParams.url))
        viewParams = { url: listView.getViewUrlFor(dataRef.current[rowIndex]) as string, needAfterScreenUrl: false, checkAllOperation: false };

      if (!viewParams.url) {
        let openParams = listView.getOpenUrlFor(dataRef.current[rowIndex]);
        if (!openParams || _.isEmpty(openParams)) {
          //ISF List의 Row를 더블클릭하면 ISF Detail이 열리도록 추가
          if(listView.getListViewTitle('') === 'ISF List') {
            try {
              const apiResult = await appContext.api.getIsfLink('', dataRef.current[rowIndex].id, dataRef.current[rowIndex].isfType);

              if (apiResult && apiResult.success) {
                  //apiResult.result.item2 : auto Login 여부 (1 : true, 0: false)
                  if(!appContext.user?.isSuntekAdmin) await appContext.api.saveSiteAccessLog(SiteLogType.IsfWeb, apiResult.result.item2 ? 1 : 0);
                    window.open('https://imanifest.suntekscm.com/Home/AutoLoginFms?args=' + apiResult.result.item1);
                  //  window.open('https://imantest.suntekscm.co.kr/Home/AutoLoginFms?args=' + apiResult.result.item1); // Test
                  // window.open('http://localhost:4649/Home/AutoLoginFms?args=' + apiResult.result.item1); // Debug
              }
            } catch (error) {
                console.error('ISF Detail Error:', error);
            }
          }
          return;
        } else {
          let left = (document.body.offsetWidth / 2) - (980 / 2);
          left += window.screenLeft;

          window.open(`${config.serverUrl}report/logViewer?jwt=${appContext.api.getAuthKey()}` + openParams,
            '_blank',
            `width=980, height=${window.screen.availHeight},left=${left}, top=0, scrollbars=yes`
          );
        }
        return;
      }

      moveToOpenedScreenInAnyOperationStartingWithUrl(viewParams.url, [viewParams.url, viewParams.url.replace('/view', '/edit')], viewParams.needAfterScreenUrl);
    }
  }

  /**
   * Callback function when table header is being clicked. Change ordering accordingly.
   *
   * @param {*} c
   * @return {*} 
   */
  function onHeaderClick(c: any) {
    if (!c || !c.id || c.id === 'indicator' || !c.sortable || !pageNum) {
      return;
    }

    let newOrder = { orderBy: c.id, orderByAscending: true };

    if (!listOrderBy || listOrderBy.orderBy !== c.id) {
      setListOrderBy(newOrder);
    } else {
      newOrder = { orderBy: listOrderBy.orderBy, orderByAscending: !listOrderBy.orderByAscending };
      setListOrderBy(newOrder);
    }

    let newOrderStr = `${c.id} ASC`;

    if (listOrderBy && listOrderBy.orderBy === c.id) {
      newOrderStr = listOrderBy.orderByAscending ? `${c.id} DESC` : `${c.id} ASC`;
    }
    headerOrderByText.current = newOrderStr;

    if (currentCount <= listViewPagePerBatch * listViewPageSize) {
      const sortResult = sortListData(dataRef.current ?? [], newOrder, listView.getTableSettings(opType, systemSettings, suntekSettings).columns);
      headerClickRef.current = true;
      if (sortResult.sorted) {
        if (_.isEqual(dataRef.current, sortResult.data)) setTick(tick + 1);
        setData(sortResult.data, currentCount);
      }
    }
    else {
      loadDataWithCurrentFilterSettings(pageNum, true, false);
    }
  }

  async function exportIntoExcel() {
    const table = document.getElementById(listDomId);
    const rowCount = table?.querySelectorAll('div[role="row"]').length??0;
    
    let jobname = job.jobName;
    if (listDomId.includes('invoice-warehousing')) jobname = JobNames.WHInvoice;
    else if (listDomId.includes('bill-warehousing')) jobname = JobNames.WHBill;
    else if (listDomId.includes('crdb-warehousing')) jobname = JobNames.WHCrDb;

    if (rowCount === 1) showGlobalOkDialog(<p>No data to Export</p>);

    //전체 데이터 갯수가 listViewPageSize * listViewPagePerBatch (300)개 이하면 listDomId를 사용
    //Customized List는 따로 검색기능이 없음. Excel Export시에는 무조건 모든 List가 나와야됨
    else if (currentCount <= listViewPageSize * listViewPagePerBatch && jobname !== JobNames.CustomizedList) {
      exportDomTableIntoExcel(listDomId, `${operationName}-${title}`, systemSettings?.userId ?? '', jobname, job.generateListView(), appContext, [], currentUrl);
    }
    //전체 데이터 갯수가 300개 보다 많으면 다시 Search 후 결과 데이터를 사용 (limit: 6000)
    else { 
      const searchWithHeaderOrder = {
      ...search,
      headerOrderBy: headerOrderByText.current,
     };
      const searchListData =  await listView.exportFormatDataWithExcel(
         appContext.api,
         searchWithHeaderOrder,
         1,
         0,
         1,
         6000,
         opType
      );
      exportDomTableIntoExcel(listDomId, `${operationName}-${title}`, systemSettings?.userId ?? '', jobname, job.generateListView(), appContext, searchListData);
    }
  }

  function jobListViewBeforePager() {
    // if (appContext.appInfoAreaContent !== infoAreaContent)
    //   setInfoAreaContent(appContext.appInfoAreaContent);

    return (
      <div>
        {/* {additionalCommands &&
          <>
            {additionalCommands}&nbsp;&nbsp;
          </>
        } */}
        {false && listView.hasMemo() &&
          <div className='ifms-common-no-vertical-separator'>
            <IconButton
              name='list-memoLog'
              onClick={() => {
                //setHideLogHistory(true);
                setInfoAreaContent(appContext.appInfoAreaContent === 'memo' ? 'none' : 'memo');
              }}
              onMouseOver={() => isMouseOverRef.current = true}
              onMouseEnter={() => isMouseOverRef.current = true}
              onMouseLeave={() => isMouseOverRef.current = false}
              data-tooltip={constants.Document_Server}
              icon = {appContext.appInfoAreaContent === 'memo' ? MemoIcon2_D : MemoIcon2}
              isActive = {appContext.appInfoAreaContent === 'memo'}
              >
              {/* <div style={{ marginTop: '-4px' }}>
                <ToggleButton isHovered={isMouseOverRef.current} value={appContext.appInfoAreaContent === 'memo'}
                />
              </div> */}
            </IconButton>
            {/*&nbsp;
             <BlackInverseButton
              name='list-logHistory'
              size='small'
              data-tooltip="Log History"
              disabled={!listView.useLogHistory()}
              notsetbackcolorwhendisabled={true}
              onClick={() => {
                if (infoAreaContent === 'none') {
                  setHideLogHistory(false);
                  setInfoAreaContent('memo');
                }
                else
                  setHideLogHistory(!hideLogHistory);
              }}
              >
              <img
                height="23px"
                width="23px"
                src={
                  listView.useLogHistory()
                    ? hideLogHistory
                      ? logHistoryOffIcon
                      : logHistoryOnIcon
                    : logHistoryDisabledIcon
                }
                alt="Log History"
                style={{ position: 'relative', top: '-3px' }}
              />
            </BlackInverseButton> */}
          </div>
        }
      </div>
    );
  }

  function jobListViewBeforeConfigureButton() {
    const hasExcelExportSec = getSecBySecTask(appContext, FmsSecTask.ToolExportToExcel, SecType.Read);

    const isSuntkKorCustomer = jobListViewContext.opType === 'customer' && appContext.suntekSettings?.company?.suntekKor;

      if (!isSuntkKorCustomer && ((jobListViewContext.opType === 'customer' && !(appContext.suntekSettings?.company?.companyId === "FSSUNTEK" || appContext.suntekSettings?.company?.companyId === "IFMSSUNTEK" || appContext.user?.isSuntekAdmin ))  || job.jobName === "BREC" || job.jobName === "BKTF")) {
      return null;
    }

    return (
      <>
        {listView.renderBeforeConfigureButton()}
        {hasExcelExportSec &&
          // <BlackInverseButton data-tooltip="Export into Excel" name='export_into_excel_button' size="large" onClick={exportIntoExcel}>
          //   <img height="18px" src={!appContext.useDarkMode ? excelIcon : excelIconGreen} alt="Export into Excel" />
          // </BlackInverseButton>
          <IconButton style={{alignItems: 'center', justifyContent: 'center'}} iconHeight="24px" name='export_into_excel_button' onClick={exportIntoExcel} data-tooltip="Export into Excel"  icon={!appContext.useDarkMode ? excelIcon2 : excelIconGreen2 }/>
        }
      </>
    );
  }

  const tableOptions: ApplicableTableSettings = {
    customTableId: listDomId,
    ref: tableRef,
    selectable: false,
    tableName: listDomId,
    ...listView.getTableSettings(opType, systemSettings, suntekSettings),
    useStorage: true,
    data: data.map((x: any) => ({ ...x })),
    rowRenderers: listView.getRowRenderers(opType, systemSettings, suntekSettings, data.length, appContext.useDarkMode),
    orderBy: [listOrderBy],
    isListView: true,
    onHeaderClick,
    useHighlightCellOnMouseover: false,
    rowProps: (row: any) => {
      if (jobName === JobNames.RptCustomerShipment) {
        let backgroundColor = '';
        backgroundColor = StatusBackColor(data[row.index]);
        if (backgroundColor) {
          return {
            style: { backgroundColor }
          };
        }
      } 
      else {
        if (row.index % 2 !== 0) {
          let backgroundColor = '';
          switch (appContext.backgroundColor) {
            case 'W': backgroundColor = '#E4E6EB'; break;
            case 'A': backgroundColor = '#E5FFE5'; break;
            case 'I': backgroundColor = '#FFFFE3'; break;
          }

          if (backgroundColor) {
            return {
              style: { backgroundColor }
            };
          }
        }
      }
      
      return undefined;
    },
    useSetClickedCellWhenClick: ['Ocean Import', 'Ocean Export', 'Other Shipment', 'Container List', 'Air Import'].includes(operationName ?? ''),
    onInitialized: () => { setInitialized(true); },
    paging: {
      pageSize: listViewPageSize,
      pageCount: Math.ceil(currentCount / listViewPageSize),
      pagePerBatch: listViewPagePerBatch,
      onPageSelect: (page: number) => {
        loadDataWithCurrentFilterSettings(page, false, false);
      },
      totalDataCount: currentCount,
      displayTotalDataCount: fastStorage[`${listDomId}-displayTotalDataCount`],
      pageNum,
      labelStyle: {
        hide: false,
        isFilterEmpty: isFilterObjectEmpty(search),
        emptyLabel: 'Recent',
      },
    },
  };

  const RenderedTable = useMemo(() => <SmartTable /*key={search?.version ?? 0}*/ {...tableOptions} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [initialized, tick, currentCount, pageNum]);

  const additionalCommands = listView.renderAdditionalCommands();

  return (
    <JobListViewContext.Provider value={jobListViewContext}>
      <div style={{ width: '100%' }} className={listDomWrapperId}>
        <ScreenTitleBar
          closeScreen={closeScreen}
          resizeTitleLabel={listView.resizetitleLabel()}
          useDarkMode={appContext.useDarkMode}
          refresh={refresh}
          title={title}
          beforePager={jobListViewBeforePager()}
          commandsAfterFilterBtn={
            <>
              {/* {appContext.suntekSettings?.company?.billingsTabVersion === 2 ? <div className={!appContext.useDarkMode ? "ifms-common-vertical-separator" : "ifms-common-vertical-separator-darkmode"} /> : <></>} */}
              <div className='ifms-common-no-vertical-separator' style={{gap:'16px'}}>
                {listView.commandsAfterFilterBtn()}
                {listView.transactionImpactBtn(appContext.user?.id ?? '')}
                <div style={{ display: 'flex' }}><FilterButton /></div>
              </div>
            </>
          }
          initializeColumnsDialog={initializeColumnsDialog}
          rightBeforeConfigureButton={jobListViewBeforeConfigureButton()}
          rightAfterConfigureButton={
            <div className="ifms-common-no-vertical-separator">
              {additionalCommands &&
                <>
                  {additionalCommands}
                </>
              }
            </div>
          }
        >
          {listView.renderCommands(appContext)}
        </ScreenTitleBar>

        {RenderedTable}

        {listView.renderExtras(hideLogHistory)}
        {listView.renderRowExtras(rowExtrasId, jobListViewContext, clickedCellRef.current, appContext)}
      </div>

      <div id='last-selectedIndex' hidden>{selectedIndex ?? 0}</div>
      <div id='last-selectedId' hidden>{selectedElement?.id ?? 0}</div>
      <div id='last-selectedOiMblId' hidden>{selectedElement?.oiMblId ?? 0}</div>
      <div id='last-selectedRlsStat' hidden>{selectedElement?.rlsStat ?? ''}</div>
      <div id='last-selectedOihblNo' hidden>{selectedElement?.hblNo ?? ''}</div>
      {/* ptk260921: Added last-selectedAmsBlNo, last-selectedCreateBy for ISF filing */}
      <div id='last-selectedAmsBlNo' hidden>{selectedElement?.amsBlNo ?? ''}</div>
      <div id='last-selectedCreateBy' hidden>{selectedElement?.u1ID ?? ''}</div>
      <RightClickContextMenu />
    </JobListViewContext.Provider>
  );
}

const JobListView = () => {
  const location = useLocation();
  let parentOp = '';

  if (location.search && location.search.indexOf('parentOp') > -1) {
    parentOp = location.search.replace('?parentOp=', '');
  }

  const { jobName, opType } = useParams<JobListViewParams>();

  /**
   * Implementation note:
   *   key is important in this case in order to force full List view rerender for different parameters.
  */
  return (
    <JobListViewImpl jobName={jobName} opType={opType} parentOp={parentOp} key={`${jobName}-${opType ?? 'common'}-list`} />
  );
};
export default JobListView;
